
import { supabase } from '@/integrations/supabase/client';
import { Task } from '@/types/task';

// This is an unsupported table schema, but we can define custom types
interface UserTaskRow {
  id: string;
  user_id: string;
  title: string;
  duration: number;
  scheduled_time: string;
  completed: boolean;
  created_at: string;
  completed_at?: string | null;
}

export const syncTasksWithSupabase = async (tasks: Task[]): Promise<Task[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('User not authenticated, skipping sync');
      return tasks;
    }
    
    console.log('Starting task sync with Supabase for user:', user.id);
    
    // First fetch existing tasks from Supabase
    const { data: existingTasks, error: fetchError } = await supabase
      .from('user_tasks')
      .select('*') as { data: UserTaskRow[] | null, error: any };
      
    if (fetchError) {
      console.error('Error fetching existing tasks:', fetchError);
      throw fetchError;
    }
    
    console.log('Fetched existing tasks from Supabase:', existingTasks?.length || 0);
    
    // For each local task, check if it exists in Supabase
    for (const task of tasks) {
      const existingTask = existingTasks?.find(et => et.id === task.id);
      
      // Convert JavaScript Date objects to ISO strings if needed
      const completedAt = task.completedAt ? task.completedAt : null;
      
      if (existingTask) {
        // Update existing task
        console.log('Updating existing task in Supabase:', task.id);
        const { error } = await supabase
          .from('user_tasks')
          .update({
            title: task.title,
            duration: task.duration,
            scheduled_time: task.scheduledTime,
            completed: task.completed || false
            // Removing completed_at from the update as it might not exist in the schema
          })
          .eq('id', task.id) as { error: any };
          
        if (error) {
          console.error('Error updating task:', error);
        }
      } else {
        // Insert new task
        console.log('Inserting new task in Supabase:', task.id);
        const { error } = await supabase
          .from('user_tasks')
          .insert({
            id: task.id,
            user_id: user.id,
            title: task.title,
            duration: task.duration,
            scheduled_time: task.scheduledTime,
            completed: task.completed || false
            // Removing completed_at from the insert as it might not exist in the schema
          }) as { error: any };
          
        if (error) {
          console.error('Error inserting task:', error);
        }
      }
    }
    
    // Fetch updated tasks to return
    const { data: updatedTasks, error: updatedError } = await supabase
      .from('user_tasks')
      .select('*')
      .eq('user_id', user.id) as { data: UserTaskRow[] | null, error: any };
      
    if (updatedError) {
      console.error('Error fetching updated tasks:', updatedError);
      throw updatedError;
    }
    
    console.log('Sync complete, returning updated tasks:', updatedTasks?.length || 0);
    
    return updatedTasks?.map(task => ({
      id: task.id,
      title: task.title,
      duration: task.duration,
      scheduledTime: task.scheduled_time,
      completed: task.completed,
      completedAt: task.completed_at || (task.completed ? new Date().toISOString() : undefined),
      isActive: false,
      isPaused: false,
      createdAt: task.created_at
    })) || tasks; // If something fails, return original tasks
  } catch (error) {
    console.error('Error syncing tasks with Supabase:', error);
    return tasks; // Return original tasks on error
  }
};

export const fetchUserTasks = async (): Promise<Task[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('User not authenticated, cannot fetch tasks');
      return [];
    }
    
    console.log('Fetching tasks for user:', user.id);
    
    const { data, error } = await supabase
      .from('user_tasks')
      .select('*')
      .eq('user_id', user.id) as { data: UserTaskRow[] | null, error: any };
      
    if (error) {
      console.error('Error fetching user tasks:', error);
      throw error;
    }
    
    console.log('Retrieved tasks from Supabase:', data?.length || 0);
    
    return data?.map(task => ({
      id: task.id,
      title: task.title,
      duration: task.duration,
      scheduledTime: task.scheduled_time,
      completed: task.completed,
      completedAt: task.completed_at || (task.completed ? new Date().toISOString() : undefined),
      isActive: false,
      isPaused: false,
      createdAt: task.created_at
    })) || [];
  } catch (error) {
    console.error('Error fetching user tasks:', error);
    throw error;
  }
};
